type ToggleSize = 'sm' | 'md' | 'lg';
type ToggleColor = 'primary' | 'green';

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: ToggleSize;
  color?: ToggleColor;
  'aria-label'?: string;
  'aria-pressed'?: boolean;
  className?: string;
}

const sizeConfig: Record<ToggleSize, {
  track: string;
  knob: string;
  onTranslate: string;
  offTranslate: string;
}> = {
  sm: {
    track: 'h-5 w-9',
    knob: 'h-3.5 w-3.5',
    onTranslate: 'ltr:translate-x-4 rtl:-translate-x-4',
    offTranslate: 'ltr:translate-x-0.5 rtl:-translate-x-0.5',
  },
  md: {
    track: 'h-6 w-11',
    knob: 'h-4 w-4',
    onTranslate: 'ltr:translate-x-6 rtl:-translate-x-6',
    offTranslate: 'ltr:translate-x-1 rtl:-translate-x-1',
  },
  lg: {
    track: 'h-7 w-12',
    knob: 'h-5 w-5',
    onTranslate: 'ltr:translate-x-6 rtl:-translate-x-6',
    offTranslate: 'ltr:translate-x-1 rtl:-translate-x-1',
  },
};

const colorConfig: Record<ToggleColor, string> = {
  primary: 'bg-primary-600',
  green: 'bg-green-500',
};

export default function ToggleSwitch({
  checked,
  onChange,
  disabled = false,
  size = 'md',
  color = 'primary',
  'aria-label': ariaLabel,
  className = '',
}: ToggleSwitchProps) {
  const { track, knob, onTranslate, offTranslate } = sizeConfig[size];
  const activeColor = colorConfig[color];

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 ${track} ${checked ? activeColor : 'bg-gray-300'} ${className}`}
    >
      <span
        className={`inline-block transform rounded-full bg-white shadow transition-transform ${knob} ${checked ? onTranslate : offTranslate}`}
      />
    </button>
  );
}
